/**
 * Additional generation jobs tracked beside the primary progress card.
 *
 * Only the parallel-generation account (utils/parallelGeneration) ever adds
 * entries, so for every other user this hook stays empty and idle. Each entry
 * has its own polling chain; completion side effects are delegated to the
 * owning context through callbacks.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GenerationJobPoll, GenerationJobKind, GenerationJobSummary, JobError } from '@/types/api';
import type { QuestionGenerationProgress } from '@/types/question';
import { generationJobsApi } from '@/services/api';
import {
  generationHttpStatus,
  GENERATION_RECONNECT_MESSAGE,
  GENERATION_RESUME_MESSAGE,
} from '@/utils/generationConnection';
import { generationFailureMessage } from '@/utils/generationFailure';
import { isInsufficientCreditJob } from '@/utils/insufficientCredit';
import { isOneClickJobKind, notifyOneClickJobRemoved, oneclickDraftIdFromResults } from '@/utils/oneclickJob';

export type ParallelGenerationJobStatus = GenerationJobSummary['status'] | 'gone';

export interface ParallelGenerationJob {
  jobId: string;
  workbookId: number | null;
  workbookTitle: string;
  jobKind: GenerationJobKind | null;
  status: ParallelGenerationJobStatus;
  progress: QuestionGenerationProgress;
  successCount: number;
  errorCount: number;
  /** Failed-item sample, as the primary card caches it. */
  errorDetails: Pick<JobError, 'passage_id' | 'question_type' | 'error' | 'error_code' | 'traceback'>[];
  /** The job failed on an insufficient-credit stop. */
  creditStopped: boolean;
  /** A "남은 문제 재시도" request for this row is in flight. */
  retrying: boolean;
  resultDraftId: number | null;
  message: string | null;
}

export type ParallelGenerationJobSeed = Pick<ParallelGenerationJob, 'jobId' | 'workbookId' | 'workbookTitle' | 'jobKind'>
  & Partial<Pick<ParallelGenerationJob, 'status' | 'progress' | 'successCount' | 'errorCount'>>;

interface ParallelGenerationJobCallbacks {
  /** Observation mode must not delete the account owner's completed jobs. */
  shouldAutoCleanup?: (jobId: string) => boolean;
  /** Called when a poll sees new completed items. */
  onProgress: (job: GenerationJobPoll, entry: ParallelGenerationJob) => void;
  /** Called once when the job reaches completed / failed / cancelled. */
  onFinished: (job: GenerationJobPoll, entry: ParallelGenerationJob) => void | Promise<void>;
  /** Stops a running job on the server; true once nothing is running. */
  stopJob: (jobId: string) => Promise<boolean>;
}

const POLL_INTERVAL_MS = 5000;
const POLL_ERROR_RETRY_MS = 15000;
const AUTO_CLOSE_MS = 5000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const LIVE_STATUSES = new Set(['queued', 'processing', 'orphaned']);
const GONE_MESSAGE = '작업 조회가 종료되었습니다. 저장된 결과를 확인해주세요.';
const CANCELLED_MESSAGE = '생성이 취소되었습니다.';

export function isLiveParallelJob(entry: Pick<ParallelGenerationJob, 'status'>): boolean {
  return LIVE_STATUSES.has(entry.status);
}

/** The primary card's queue box / resume notice, as one line set for a row. */
function waitingMessage(job: GenerationJobPoll): string | null {
  const serverMessage = job.queue_message?.trim() || null;
  if (job.status === 'orphaned') return serverMessage ?? GENERATION_RESUME_MESSAGE;
  if (job.status !== 'queued') return null;
  const position = typeof job.queue_position === 'number' && job.queue_position > 0
    ? `현재 대기 순서: ${job.queue_position}번째`
    : null;
  return [serverMessage ?? '다른 작업이 진행 중입니다', position].filter(Boolean).join('\n');
}

function emptyEntry(seed: ParallelGenerationJobSeed): ParallelGenerationJob {
  return {
    status: 'processing',
    progress: { current: 0, total: 0, percentage: 0 },
    successCount: 0,
    errorCount: 0,
    errorDetails: [],
    creditStopped: false,
    retrying: false,
    resultDraftId: null,
    message: null,
    ...seed,
  };
}

export function useParallelGenerationJobs(callbacks: ParallelGenerationJobCallbacks) {
  const [parallelJobs, setParallelJobs] = useState<ParallelGenerationJob[]>([]);
  const entriesRef = useRef(new Map<string, ParallelGenerationJob>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Per-job chain token: a poll resumed after `await` bails once superseded.
  const tokensRef = useRef(new Map<string, number>());
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const publish = useCallback(() => {
    setParallelJobs(Array.from(entriesRef.current.values()));
  }, []);

  const putEntry = useCallback((entry: ParallelGenerationJob) => {
    entriesRef.current.set(entry.jobId, entry);
    publish();
  }, [publish]);

  const stopChain = useCallback((jobId: string): number => {
    const timer = timersRef.current.get(jobId);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(jobId);
    const token = (tokensRef.current.get(jobId) ?? 0) + 1;
    tokensRef.current.set(jobId, token);
    return token;
  }, []);

  const untrackParallelJob = useCallback((jobId: string) => {
    stopChain(jobId);
    if (entriesRef.current.delete(jobId)) publish();
  }, [publish, stopChain]);

  const pollRef = useRef<(jobId: string, token: number) => Promise<void>>(async () => {});

  const schedule = useCallback((jobId: string, token: number, delay: number) => {
    if (tokensRef.current.get(jobId) !== token) return;
    timersRef.current.set(jobId, setTimeout(() => {
      void pollRef.current(jobId, token);
    }, delay));
  }, []);

  pollRef.current = async (jobId: string, token: number) => {
    const isCurrent = () => tokensRef.current.get(jobId) === token && entriesRef.current.has(jobId);
    if (!isCurrent()) return;
    let job: GenerationJobPoll;
    try {
      job = await generationJobsApi.getJob(jobId);
    } catch (error) {
      if (!isCurrent()) return;
      const entry = entriesRef.current.get(jobId)!;
      if (generationHttpStatus(error) === 404) {
        timersRef.current.delete(jobId);
        putEntry({ ...entry, status: 'gone', message: GONE_MESSAGE });
        notifyOneClickJobRemoved(jobId, entry.jobKind);
        return;
      }
      putEntry({ ...entry, message: GENERATION_RECONNECT_MESSAGE });
      schedule(jobId, token, POLL_ERROR_RETRY_MS);
      return;
    }
    if (!isCurrent()) return;

    const previous = entriesRef.current.get(jobId)!;
    const polled: ParallelGenerationJob = {
      ...previous,
      status: job.status,
      progress: { current: job.completed, total: job.total, percentage: job.percentage },
      successCount: job.success_count,
      errorCount: job.error_count,
      errorDetails: job.errors ?? [],
      message: waitingMessage(job),
    };
    putEntry(polled);
    if (job.completed > previous.progress.current) {
      callbacksRef.current.onProgress(job, polled);
    }
    if (!TERMINAL_STATUSES.has(job.status)) {
      schedule(jobId, token, POLL_INTERVAL_MS);
      return;
    }

    timersRef.current.delete(jobId);
    const finished: ParallelGenerationJob = {
      ...polled,
      resultDraftId: isOneClickJobKind(job.job_kind ?? previous.jobKind)
        ? oneclickDraftIdFromResults(job.results)
        : null,
      creditStopped: job.status === 'failed' && isInsufficientCreditJob(job),
      message: job.status === 'failed'
        ? generationFailureMessage(job.error_message || job.errors?.[0]?.error)
        : job.status === 'cancelled' ? CANCELLED_MESSAGE : null,
    };
    putEntry(finished);
    try {
      await callbacksRef.current.onFinished(job, finished);
    } catch (error) {
      console.error('[ParallelGenerationJobs] Completion handling failed:', jobId, error);
    }
    if (job.status === 'completed' && job.error_count === 0 && isCurrent()) {
      // A clean completion closes itself like the primary card does.
      timersRef.current.set(jobId, setTimeout(() => {
        if (!isCurrent()) return;
        untrackParallelJob(jobId);
        if (finished.jobKind === 'oneclick_ocr' && finished.resultDraftId == null) return;
        if (callbacksRef.current.shouldAutoCleanup?.(jobId) === false) return;
        void generationJobsApi.deleteJob(jobId).catch((deleteError) => {
          console.warn('[ParallelGenerationJobs] Failed to delete completed job:', jobId, deleteError);
        });
      }, AUTO_CLOSE_MS));
    }
  };

  const trackParallelJob = useCallback((seed: ParallelGenerationJobSeed) => {
    if (entriesRef.current.has(seed.jobId)) return;
    putEntry(emptyEntry(seed));
    schedule(seed.jobId, stopChain(seed.jobId), POLL_INTERVAL_MS);
  }, [putEntry, schedule, stopChain]);

  const patchParallelJob = useCallback((
    jobId: string,
    update: Partial<Pick<ParallelGenerationJob, 'message' | 'retrying'>>,
  ) => {
    const entry = entriesRef.current.get(jobId);
    if (entry) putEntry({ ...entry, ...update });
  }, [putEntry]);

  const captureParallelJobScope = useCallback((jobId: string) => {
    const token = tokensRef.current.get(jobId);
    return () => entriesRef.current.has(jobId) && tokensRef.current.get(jobId) === token;
  }, []);

  /** Poll an existing row again right away (a retry found it still running). */
  const resumeParallelJob = useCallback((jobId: string) => {
    if (!entriesRef.current.has(jobId)) return;
    schedule(jobId, stopChain(jobId), 0);
  }, [schedule, stopChain]);

  /** The row now follows the job a retry created, keeping its place in the list. */
  const retargetParallelJob = useCallback((
    previousJobId: string,
    seed: Omit<ParallelGenerationJobSeed, 'workbookId' | 'workbookTitle' | 'jobKind'>
      & Partial<Pick<ParallelGenerationJobSeed, 'jobKind'>>,
  ) => {
    const previous = entriesRef.current.get(previousJobId);
    if (!previous) return false;
    stopChain(previousJobId);
    const next = emptyEntry({
      workbookId: previous.workbookId,
      workbookTitle: previous.workbookTitle,
      jobKind: previous.jobKind,
      ...seed,
    });
    entriesRef.current = new Map(
      Array.from(entriesRef.current, ([id, entry]) => (id === previousJobId ? [next.jobId, next] : [id, entry])),
    );
    publish();
    schedule(next.jobId, stopChain(next.jobId), POLL_INTERVAL_MS);
    return true;
  }, [publish, schedule, stopChain]);

  const dismissParallelJob = useCallback((jobId: string) => {
    const entry = entriesRef.current.get(jobId);
    untrackParallelJob(jobId);
    if (entry?.jobKind === 'oneclick_ocr' && entry.status === 'completed' && entry.resultDraftId == null) return;
    if (callbacksRef.current.shouldAutoCleanup?.(jobId) === false) return;
    void generationJobsApi.deleteJob(jobId).catch((deleteError) => {
      console.warn('[ParallelGenerationJobs] Failed to delete dismissed job:', jobId, deleteError);
    });
  }, [untrackParallelJob]);

  const cancelParallelJob = useCallback(async (jobId: string) => {
    const entry = entriesRef.current.get(jobId);
    if (!entry) return;
    if (isLiveParallelJob(entry)) {
      const token = stopChain(jobId);
      const stopped = await callbacksRef.current.stopJob(jobId);
      if (tokensRef.current.get(jobId) !== token || !entriesRef.current.has(jobId)) return;
      if (!stopped) {
        // Still running (and charging): keep the row and resume polling.
        schedule(jobId, token, 0);
        return;
      }
    }
    untrackParallelJob(jobId);
    if (entry.jobKind === 'oneclick_ocr' && entry.status === 'completed' && entry.resultDraftId == null) return;
    if (callbacksRef.current.shouldAutoCleanup?.(jobId) === false) return;
    try {
      await generationJobsApi.deleteJob(jobId);
    } catch (deleteError) {
      console.warn('[ParallelGenerationJobs] Failed to delete cancelled job:', jobId, deleteError);
    }
  }, [schedule, stopChain, untrackParallelJob]);

  const clearParallelJobs = useCallback(() => {
    for (const jobId of Array.from(entriesRef.current.keys())) {
      stopChain(jobId);
    }
    if (entriesRef.current.size > 0) {
      entriesRef.current.clear();
      publish();
    }
  }, [publish, stopChain]);

  useEffect(() => () => {
    for (const jobId of Array.from(tokensRef.current.keys())) {
      tokensRef.current.set(jobId, (tokensRef.current.get(jobId) ?? 0) + 1);
    }
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
  }, []);

  return {
    parallelJobs,
    trackParallelJob,
    untrackParallelJob,
    patchParallelJob,
    captureParallelJobScope,
    resumeParallelJob,
    retargetParallelJob,
    cancelParallelJob,
    dismissParallelJob,
    clearParallelJobs,
  };
}
